import type {
  TrainingGroundAnalytics,
  TrainingGroundAnalyticsSubjectBreakdown,
  TrainingGroundMistakesSummary,
} from "@app/training-ground/utils/training-ground-state";

/** Learner-safe Training Ground summaries made available to either home theme. */
export type TrainingGroundHomeData = {
  studentId: number;
  mistakes?: TrainingGroundMistakesSummary;
  analytics?: TrainingGroundAnalytics;
};

/** The one highest-priority Training Ground action shown on student home. */
export type TrainingGroundHomeGuidance =
  | { kind: "resume" }
  | { kind: "review"; dueCount: number }
  | {
      kind: "targetSubject";
      subject: TrainingGroundAnalyticsSubjectBreakdown["subject"] & {
        id: number;
      };
      accuracy: number;
    }
  | { kind: "adaptive"; accuracy: number }
  | { kind: "start" };

function validWeakSubject(
  breakdown: TrainingGroundAnalyticsSubjectBreakdown,
): breakdown is TrainingGroundAnalyticsSubjectBreakdown & {
  subject: NonNullable<TrainingGroundAnalyticsSubjectBreakdown["subject"]> & {
    id: number;
  };
} {
  return (
    Number.isSafeInteger(breakdown.subject?.id) &&
    Number(breakdown.subject?.id) > 0 &&
    Number.isFinite(breakdown.accuracy) &&
    breakdown.accuracy < 60 &&
    breakdown.totalQuestions > 0
  );
}

/**
 * Choose one next action without auto-starting practice.
 *
 * Resume always wins, followed by due spaced review, targeted weak-subject
 * practice, adaptive practice for returning learners, then a first round.
 */
export function resolveTrainingGroundHomeGuidance({
  hasDraft,
  mistakes,
  analytics,
}: {
  hasDraft: boolean;
  mistakes?: TrainingGroundMistakesSummary;
  analytics?: TrainingGroundAnalytics;
}): TrainingGroundHomeGuidance {
  if (hasDraft) return { kind: "resume" };

  const dueCount = Number(mistakes?.dueCount || 0);

  if (Number.isSafeInteger(dueCount) && dueCount > 0) {
    return { kind: "review", dueCount };
  }

  const weakestSubject = analytics?.subjects
    .filter(validWeakSubject)
    .reduce<TrainingGroundAnalyticsSubjectBreakdown | undefined>(
      (weakest, subject) =>
        !weakest || subject.accuracy < weakest.accuracy ? subject : weakest,
      undefined,
    );

  if (weakestSubject && validWeakSubject(weakestSubject)) {
    return {
      kind: "targetSubject",
      subject: weakestSubject.subject,
      accuracy: weakestSubject.accuracy,
    };
  }

  if (
    analytics &&
    Number.isSafeInteger(analytics.completedRounds) &&
    analytics.completedRounds > 0
  ) {
    return {
      kind: "adaptive",
      accuracy: Number.isFinite(analytics.accuracy) ? analytics.accuracy : 0,
    };
  }

  return { kind: "start" };
}

/** Add a subject only when targeted guidance carries a validated positive id. */
export function getTrainingGroundHomeHref(
  baseUrl: string,
  guidance: TrainingGroundHomeGuidance,
): string {
  return guidance.kind === "targetSubject"
    ? `${baseUrl}?subject=${guidance.subject.id}`
    : baseUrl;
}
